/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/usePickupGroupForm.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for managing Pickup Group form state, validation, and submission logic.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { createPickupGroup, updatePickupGroup } from '../api/pickupGroupsApi';
import { createDefaultFormData } from '../utils/formDefaults';
import { generateContextFromName } from '../utils/contextHelpers';
import type { PickupGroupFormState, PickupGroupMember, ValidationErrors } from '../types';

interface UsePickupGroupFormOptions {
  formData: PickupGroupFormState;
  setFormData: React.Dispatch<React.SetStateAction<PickupGroupFormState>>;
  members: PickupGroupMember[];
  setMembers: React.Dispatch<React.SetStateAction<PickupGroupMember[]>>;
  originalFormData: PickupGroupFormState;
  originalMembers: PickupGroupMember[];
  isEditMode: boolean;
  pickupGroupId?: string;
  onLoadPickupGroup?: (id: string) => Promise<void>;
}

export function usePickupGroupForm({
  formData,
  setFormData,
  members,
  setMembers,
  originalFormData,
  originalMembers,
  isEditMode,
  pickupGroupId,
  onLoadPickupGroup
}: UsePickupGroupFormOptions) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError, showInfo } = useNotifications();
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSaving, setIsSaving] = useState(false);

  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    const dataChanged = JSON.stringify(formData) !== JSON.stringify(originalFormData);
    const membersChanged = JSON.stringify(members) !== JSON.stringify(originalMembers);
    return dataChanged || membersChanged;
  }, [formData, members, originalFormData, originalMembers]);

  // Validation
  const validateForm = useCallback(() => {
    const newErrors: ValidationErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('pickupGroups.fields.name.required');
    }

    if (formData.priority < 1 || formData.priority > 9999) {
      newErrors.priority = t('pickupGroups.fields.priority.min');
    }

    if (formData.settings.pickup_timeout < 5 || formData.settings.pickup_timeout > 120) {
      newErrors.pickupTimeout = t('pickupGroups.fields.pickupTimeout.min');
    }

    // Filter valid members first
    const validMembers = members.filter(member => member.extension && member.extension.trim());

    if (validMembers.length === 0) {
      newErrors.members = t('pickupGroups.fields.members.required');
    }

    members.forEach((member, index) => {
      if (!member.extension || !member.extension.trim()) {
        newErrors[`member-${index}`] = t('pickupGroups.fields.members.extensionRequired');
      }
    });

    // Check for duplicates only among valid members
    const validExtensions = validMembers.map(member => member.extension.trim());
    const uniqueExtensions = new Set(validExtensions);
    if (uniqueExtensions.size !== validExtensions.length) {
      newErrors.members = t('pickupGroups.fields.members.unique');
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      showError(t('pickupGroups.messages.validationError'));
      return false;
    }

    return true;
  }, [formData, members, t, showError]);

  // Handle save
  const handleSave = useCallback(async () => {
    // First check: ensure we have members before validation
    const validMembersBeforeValidation = members.filter(member => member.extension && member.extension.trim());
    if (validMembersBeforeValidation.length === 0) {
      setErrors({ members: t('pickupGroups.fields.members.required') });
      showError(t('pickupGroups.messages.validationError'));
      return undefined;
    }

    if (!validateForm()) {
      return undefined;
    }

    setIsSaving(true);
    try {
      // Filter out invalid members (empty extensions) before sending
      const validMembers = members
        .filter(member => member && member.extension && typeof member.extension === 'string' && member.extension.trim())
        .map(member => ({
          extension: member.extension.trim(),
          allowPickup: member.allowPickup ?? true,
          isMember: member.isMember ?? true
        }));

      // Ensure we have at least one valid member (validation should catch this, but double-check)
      if (validMembers.length === 0) {
        setErrors({ members: t('pickupGroups.fields.members.required') });
        showError(t('pickupGroups.messages.validationError'));
        setIsSaving(false);
        return undefined;
      }

      // Extract target extensions from valid members for settings
      const targetExtensions = validMembers.map(member => member.extension);

      // Merge settings with target_extensions
      const settingsWithTargets = {
        ...formData.settings,
        target_extensions: targetExtensions
      };

      const payload = {
        name: formData.name.trim(),
        context: formData.context || generateContextFromName(formData.name.trim()),
        priority: formData.priority,
        skipBusy: formData.skipBusy,
        enabled: formData.enabled,
        settings: settingsWithTargets,
        members: validMembers
      };

      let savedData;
      if (isEditMode && pickupGroupId) {
        savedData = await updatePickupGroup(pickupGroupId, payload);
        showSuccess(t('pickupGroups.messages.saveSuccess'));
        if (onLoadPickupGroup && savedData.id) {
          await onLoadPickupGroup(savedData.id.toString());
        }
      } else {
        savedData = await createPickupGroup(payload);
        showSuccess(t('pickupGroups.messages.saveSuccess'));
        // Note: Navigation after create is now handled by the dropdown actions
        // in PickupGroupsFormView, so we don't navigate here automatically
      }

      return savedData;
    } catch (error) {
      console.error('Error saving pickup group:', error);
      showError(error.message || t('pickupGroups.messages.saveError'));
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [
    formData,
    members,
    validateForm,
    isEditMode,
    pickupGroupId,
    hasChanges,
    showSuccess,
    showError,
    showInfo,
    t,
    onLoadPickupGroup
  ]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    if (isEditMode) {
      setFormData({ ...originalFormData });
      setMembers([...originalMembers]);
      setErrors({});
    } else {
      const defaults = createDefaultFormData();
      setFormData(defaults);
      setMembers([]);
      setErrors({});
    }
  }, [isEditMode, originalFormData, originalMembers, setFormData, setMembers]);

  return {
    errors,
    setErrors,
    isSaving,
    hasChanges,
    validateForm,
    handleSave,
    handleCancel
  };
}

